import React, { useEffect } from "react";
import jexcel, { JExcelElement } from "jexcel";

import "../../../../node_modules/jexcel/dist/jexcel.css";
import { ITeacher, ITeacherForRegister } from "../../../state";
import { ModalContext } from "../../../libs/ModalProvider";
import Confirm from "../../../molecules/Confirm";
import AlertModal from "../../../molecules/AlertModal";

import styles from "./TeacherSpreadSheet.module.scss";

export interface TeacherSpreadSheetProps {
  title: string;
  calling: boolean;
  error: string;
  teachers: ITeacher[];
  save: (Teachers: ITeacherForRegister[]) => void;
}
const Fields = {} as { [key: string]: number };
Fields["姓"] = 0;
Fields["名"] = 1;
Fields["姓(カナ)"] = 2;
Fields["名(カナ)"] = 3;
Fields["メールアドレス"] = 4;
Fields["学校管理者"] = 5;
Fields["授業報告"] = 6;
Fields["顔写真"] = 7;
Fields["チェック結果"] = 8;

const INITIAL_ROW_VALUES = [
  null,
  null,
  null,
  null,
  null,
  false,
  true,
  null,
  null,
];

export const TeacherSpreadSheet = (props: TeacherSpreadSheetProps) => {
  const ctx = React.useContext(ModalContext);
  const jexcelRef = React.useRef<HTMLDivElement>(null);
  const [state, setState] = React.useState({
    sheet: null as null | JExcelElement,
    tableData: [[...INITIAL_ROW_VALUES]] as
      | null
      | (string | boolean | null)[][],
  });
  const requiredFields = React.useMemo(() => {
    return ["姓", "名", "姓(カナ)", "名(カナ)", "メールアドレス"];
  }, []);
  const save = props.save;
  const colWidths = React.useMemo(
    () => [100, 100, 100, 100, 140, 80, 80, 200, 300],
    [],
  );
  const regexCheckEmail = React.useMemo(() => /\S+@\S+\.\S+/, []);

  useEffect(() => {
    const jexcelRefCurrent = jexcelRef.current;
    if (jexcelRefCurrent) {
      jexcelRefCurrent.innerHTML = "";
    }
    let sheet = null as null | JExcelElement;
    const options = {
      data: state.tableData,
      colWidths,
      columns: [
        { type: "text" as const, title: "姓*" },
        { type: "text" as const, title: "名*" },
        { type: "text" as const, title: "姓(カナ)*" },
        { type: "text" as const, title: "名(カナ)*" },
        { type: "text" as const, title: "メールアドレス*" },
        {
          type: "checkbox" as const,
          title: "学校管理者",
        },
        {
          type: "checkbox" as const,
          title: "授業報告",
        },
        { type: "image" as const, title: "顔写真" },
        { type: "text" as const, title: "チェック結果" },
      ],
      onchange: (
        _instance: HTMLElement,
        _cell: HTMLTableCellElement,
        x: number,
        y: number,
        value: any,
      ) => {
        if (x === Fields["チェック結果"]) {
          return;
        }
        // 顔写真フィールドの場合、無効な値を即座にクリアする
        if (x === Fields["顔写真"] && value) {
          const photoValue = value as string;

          // Base64形式の画像データのみをチェック
          const isValidBase64Image = /^data:image\/[a-z]+;base64,.+/.test(
            photoValue,
          );

          if (!isValidBase64Image) {
            sheet?.setValueFromCoords(x, y, "", false);
            sheet?.setValueFromCoords(
              Fields["チェック結果"],
              y,
              "顔写真は画像のみ対応しています",
              false,
            );
            return;
          }
        }
        const rec = sheet?.getRowData(Number(y));
        let result = "";
        if (rec) {
          if (
            rec[Fields["メールアドレス"]] &&
            !regexCheckEmail.test(rec[Fields["メールアドレス"]])
          ) {
            result = "不正なメールアドレスが入力されています";
          } else {
            for (const rF of requiredFields) {
              if (!rec[Fields[rF]]) {
                result = `${rF}が必要です`;
                break;
              }
            }
          }
          sheet?.setValueFromCoords(
            Fields["チェック結果"],
            Number(y),
            result,
            false,
          );
        }
      },
      oninsertrow: (
        _el: HTMLElement,
        rowNumber: number,
        _numOfRows: number,
        _rowRecords: any[],
        _insertBefore: boolean,
      ) => {
        sheet?.setRowData(rowNumber + 1, INITIAL_ROW_VALUES);
      },
    };
    sheet = jexcel(jexcelRefCurrent as any, options as any);
    setState(s => ({ ...s, sheet }));
  }, [state.tableData, colWidths, requiredFields, regexCheckEmail]);

  const addRow = React.useCallback(() => {
    state.sheet?.insertRow();
  }, [state.sheet]);

  const resetFunc = React.useCallback(() => {
    setState(prevState => ({
      ...prevState,
      tableData: [[...INITIAL_ROW_VALUES]],
    }));
  }, []);

  const saveFunc = React.useCallback(() => {
    const data = [] as ITeacherForRegister[];
    let isInvalidEmail = false;
    if (state.sheet) {
      const dataSet = state.sheet?.getData();
      dataSet.forEach(d => {
        const isAllRequiredFieldsValid = requiredFields.every(
          rF => !!d[Fields[rF]],
        );
        const emailValue = d[Fields["メールアドレス"]]
          ? (d[Fields["メールアドレス"]] as string)
          : "";
        if (emailValue && !regexCheckEmail.test(emailValue)) {
          isInvalidEmail = true;
        }

        if (isAllRequiredFieldsValid && !d[Fields["チェック結果"]]) {
          data.push({
            last_name: d[Fields["姓"]] as string,
            first_name: d[Fields["名"]] as string,
            last_name_kana: d[Fields["姓(カナ)"]] as string,
            first_name_kana: d[Fields["名(カナ)"]] as string,
            email: d[Fields["メールアドレス"]] as string,
            is_user_manager: d[Fields["学校管理者"]] as boolean,
            receive_email_enabled: d[Fields["授業報告"]] as boolean,
            photo: d[Fields["顔写真"]] as string,
          });
        }
      });
    }
    if (isInvalidEmail) {
      ctx.actions.showModal(
        <AlertModal
          closeModal={() => {
            ctx.actions.closeModal();
          }}
          content={
            <>
              <div>更新できませんでした。</div>
              <div>正しいメールアドレスを入力してください。</div>
            </>
          }
          callback={() => {
            ctx.actions.closeModal();
          }}
        />,
      );
    } else {
      save(data);
    }
  }, [save, state.sheet, regexCheckEmail, requiredFields, ctx.actions]);

  const handleSaveClick = React.useCallback(() => {
    ctx.actions.showModal(
      <Confirm
        calling={props.calling}
        closeModal={() => ctx.actions.closeModal()}
        content={
          <>
            <p className="mb-0">先生を登録します、よろしいですか？</p>
            <p>登録したメールアドレスに招待メールが送信されます</p>
          </>
        }
        callback={saveFunc}
      />,
    );
  }, [props.calling, ctx.actions, saveFunc]);

  return (
    <div className={styles.wrapper}>
      {props.error && (
        <div
          style={{
            padding: "20px",
            border: "2px solid #d84429",
            lineHeight: "1rem",
            marginBottom: "20px",
          }}
        >
          {props.error}
        </div>
      )}
      <div className={styles.header}>
        <h4>{props.title}</h4>
        <p>
          先生方を一括で登録することが可能です。
          <br />
          下記のシートに必要な情報をご記入の上、ご登録ください。
          <br />
          ※顔写真を「ダブルクリック」すると画像をアップロードできます。
        </p>
      </div>
      <div className={styles.actionButtons}>
        <button
          className={`btn btn-outline-danger me-3`}
          disabled={props.calling}
          onClick={() => {
            ctx.actions.showModal(
              <Confirm
                calling={props.calling}
                content={"データを初期状態にリセットしますか？"}
                closeModal={() => {
                  ctx.actions.closeModal();
                }}
                callback={resetFunc}
              />,
            );
          }}
        >
          リセット
        </button>
      </div>
      <div className={styles.excel} ref={jexcelRef} />
      <p className={styles.note}>
        ※「チェック結果」にエラーが表示されている行は登録されませんので、エラーを修正してから登録してください。
      </p>
      <div className={styles.action}>
        <button className="btn btn-primary" onClick={addRow}>
          追加
        </button>
        <button
          className="btn btn-primary"
          onClick={handleSaveClick}
          disabled={props.calling}
        >
          登録
        </button>
      </div>
    </div>
  );
};

export default TeacherSpreadSheet;
